/**
 * 🛥️ ADD YACHT FOR HOST DIALOG
 * Admin can add a new yacht to a host's fleet
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  MenuItem,
  Typography,
  Box,
  FormControlLabel,
  Switch,
  InputAdornment,
  Divider,
  Chip,
} from '@mui/material';
import { SailingOutlined, AttachMoney, CloudUpload, Delete } from '@mui/icons-material';
import { toast } from 'react-hot-toast';
import { adminYachtService, CreateYachtForHostRequest } from '@/services/adminYachtService';
import { CURRENCY_CONFIG, type CurrencyCode } from '@/services/vehicleApprovalService';

interface AddYachtForHostDialogProps {
  open: boolean;
  onClose: () => void;
  hostId: string;
  hostName: string;
  onSuccess?: () => void;
}

const NIGERIAN_STATES = [
  'Abia', 'Adamawa', 'Akwa Ibom', 'Anambra', 'Bauchi', 'Bayelsa', 'Benue', 'Borno', 'Cross River',
  'Delta', 'Ebonyi', 'Edo', 'Ekiti', 'Enugu', 'FCT', 'Gombe', 'Imo', 'Jigawa', 'Kaduna', 'Kano',
  'Katsina', 'Kebbi', 'Kogi', 'Kwara', 'Lagos', 'Nasarawa', 'Niger', 'Ogun', 'Ondo', 'Osun',
  'Oyo', 'Plateau', 'Rivers', 'Sokoto', 'Taraba', 'Yobe', 'Zamfara'
];

const YACHT_MANUFACTURERS = [
  'Azimut', 'Benetti', 'Ferretti', 'Princess', 'Sunseeker', 'Viking', 'Hatteras', 'Bertram',
  'Pershing', 'Riva', 'Beneteau', 'Jeanneau', 'Bavaria', 'Lagoon', 'Fountaine Pajot',
  'Sea Ray', 'Boston Whaler', 'Grady-White', 'Lürssen', 'Feadship', 'Oceanco', 'Custom'
].sort();

const YACHT_TYPES = [
  { value: 'motor_yacht', label: 'Motor Yacht' },
  { value: 'sailing_yacht', label: 'Sailing Yacht' },
  { value: 'catamaran', label: 'Catamaran' },
  { value: 'luxury_yacht', label: 'Luxury Yacht' },
  { value: 'sport_yacht', label: 'Sport Yacht' }
];

const FUEL_POLICIES = [
  { value: 'included', label: 'Fuel Included in Price' },
  { value: 'pay_as_you_go', label: 'Pay As You Go' },
  { value: 'full_tank_return', label: 'Full Tank Return' }
];

const SAFETY_CERTIFICATIONS = ['ISPS', 'ISM', 'SOLAS', 'MCA'];

export const AddYachtForHostDialog: React.FC<AddYachtForHostDialogProps> = ({
  open,
  onClose,
  hostId,
  hostName,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false);
  const [uploadedPhotos, setUploadedPhotos] = useState<{
    imageData: string;
    type: 'exterior' | 'interior' | 'cabin' | 'deck' | 'other';
    fileName: string;
    isPrimary: boolean;
  }[]>([]);

  const [formData, setFormData] = useState({
    // Yacht Details (required: name, capacity)
    name: '',
    capacity: '',
    type: '', // e.g., 'luxury', 'sport'
    description: '',
    yachtType: '' as '' | 'motor_yacht' | 'sailing_yacht' | 'catamaran' | 'luxury_yacht' | 'sport_yacht',
    year: '',
    manufacturer: '',
    model: '',
    registrationNumber: '',
    homeport: '',
    marina: '',
    features: [] as string[],

    // Specifications
    length: '',
    crew: '',
    bedrooms: '',
    bathrooms: '',
    speed: '',

    // Captain
    captainName: '',
    captainExperience: '',
    captainLanguages: [] as string[],

    // Location
    country: '',
    city: '',
    state: '',
    locationMarina: '',

    // Pricing
    hourly: '',
    halfDay: '',
    fullDay: '',
    fullDayHours: '',
    weekly: '',
    securityDeposit: '',
    currency: 'NGN' as CurrencyCode,
    fuelPolicy: '' as '' | 'included' | 'pay_as_you_go' | 'full_tank_return',
    cateringAvailable: false,
    cateringCostPerPerson: '',

    // Insurance
    hasInsurance: false,
    insuranceType: '',
    insuranceExpiryDate: '',
    hasSafetyEquipment: false,
    safetyCertifications: [] as string[],

    // Availability
    instantBooking: false,
    minimumRentalHours: '',
  });

  const handlePhotoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    if (uploadedPhotos.length + files.length > 15) {
      toast.error('Maximum 15 photos allowed for yachts');
      return;
    }

    Array.from(files).forEach((file) => {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} is too large. Max size is 10MB`);
        return;
      }

      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} is not an image file`);
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const imageData = e.target?.result as string;
        setUploadedPhotos(prev => [...prev, {
          imageData,
          type: 'exterior',
          fileName: file.name,
          isPrimary: prev.length === 0 // First photo is primary
        }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleRemovePhoto = (index: number) => {
    setUploadedPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const toggleCertification = (cert: string) => {
    setFormData(prev => ({
      ...prev,
      safetyCertifications: prev.safetyCertifications.includes(cert)
        ? prev.safetyCertifications.filter(c => c !== cert)
        : [...prev.safetyCertifications, cert]
    }));
  };

  const [newFeature, setNewFeature] = useState('');
  const addFeature = () => {
    if (newFeature.trim() && !formData.features.includes(newFeature.trim())) {
      setFormData(prev => ({
        ...prev,
        features: [...prev.features, newFeature.trim()]
      }));
      setNewFeature('');
    }
  };
  const removeFeature = (feature: string) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter(f => f !== feature)
    }));
  };

  const [newLanguage, setNewLanguage] = useState('');
  const addLanguage = () => {
    if (newLanguage.trim() && !formData.captainLanguages.includes(newLanguage.trim())) {
      setFormData(prev => ({
        ...prev,
        captainLanguages: [...prev.captainLanguages, newLanguage.trim()]
      }));
      setNewLanguage('');
    }
  };
  const removeLanguage = (lang: string) => {
    setFormData(prev => ({
      ...prev,
      captainLanguages: prev.captainLanguages.filter(l => l !== lang)
    }));
  };

  const handleSubmit = async () => {
    // Validation - only name and capacity are required
    if (!formData.name || !formData.capacity) {
      toast.error('Please provide yacht name and capacity');
      return;
    }

    setLoading(true);

    try {
      const requestData: CreateYachtForHostRequest = {
        hostId,
        yacht: {
          // Required field
          name: formData.name,
          // Basic yacht info
          type: formData.type || undefined,
          capacity: parseInt(formData.capacity), // Required - max passengers
          guests: parseInt(formData.capacity), // Same as capacity for display
          description: formData.description || undefined,
          yachtType: formData.yachtType || undefined,
          year: formData.year ? parseInt(formData.year) : undefined,
          manufacturer: formData.manufacturer || undefined,
          model: formData.model || undefined,
          registrationNumber: formData.registrationNumber || undefined,
          homeport: formData.homeport || undefined,
          marina: formData.marina || undefined,
          // Features
          features: formData.features.length > 0 ? formData.features : undefined,
          // Specifications
          length: formData.length || undefined,
          crew: formData.crew ? parseInt(formData.crew) : undefined,
          bedrooms: formData.bedrooms ? parseInt(formData.bedrooms) : undefined,
          bathrooms: formData.bathrooms ? parseInt(formData.bathrooms) : undefined,
          speed: formData.speed || undefined,
          // Captain info
          captainName: formData.captainName || undefined,
          captainExperience: formData.captainExperience || undefined,
          captainLanguages: formData.captainLanguages.length > 0 ? formData.captainLanguages : undefined,
        },
        location: {
          country: formData.country || undefined,
          city: formData.city || undefined,
          state: formData.state || undefined,
          marina: formData.locationMarina || undefined,
        },
        pricing: {
          hourly: formData.hourly ? parseFloat(formData.hourly) : undefined,
          halfDay: formData.halfDay ? parseFloat(formData.halfDay) : undefined,
          fullDay: formData.fullDay ? parseFloat(formData.fullDay) : undefined,
          fullDayHours: formData.fullDayHours ? parseInt(formData.fullDayHours) : undefined,
          weekly: formData.weekly ? parseFloat(formData.weekly) : undefined,
          securityDeposit: formData.securityDeposit ? parseFloat(formData.securityDeposit) : undefined,
          currency: formData.currency || 'NGN',
          fuelPolicy: formData.fuelPolicy || undefined,
          cateringAvailable: formData.cateringAvailable,
          cateringCostPerPerson: formData.cateringCostPerPerson ? parseFloat(formData.cateringCostPerPerson) : undefined,
        },
        insurance: {
          hasInsurance: formData.hasInsurance,
          type: formData.insuranceType || undefined,
          expiryDate: formData.insuranceExpiryDate || undefined,
          hasSafetyEquipment: formData.hasSafetyEquipment,
          safetyCertifications: formData.safetyCertifications.length > 0 ? formData.safetyCertifications : undefined,
        },
        availability: {
          instantBooking: formData.instantBooking,
          minimumRentalHours: formData.minimumRentalHours ? parseInt(formData.minimumRentalHours) : undefined,
        },
      };

      const result = await adminYachtService.createYachtForHost(requestData);

      if (result.success) {
        const yachtId = result.data?.yachtId;

        // Upload photos if any were selected
        if (yachtId && uploadedPhotos.length > 0) {
          toast.loading(`Uploading ${uploadedPhotos.length} photo${uploadedPhotos.length > 1 ? 's' : ''}...`);

          // Small delay to ensure database replication completes
          await new Promise(resolve => setTimeout(resolve, 1000));

          // Map to the format the service expects
          const mappedPhotos = uploadedPhotos.map(photo => ({
            url: photo.imageData,  // The base64 data URI
            category: photo.type,   // Map 'type' to 'category'
            isPrimary: photo.isPrimary
          }));

          const photoResult = await adminYachtService.uploadYachtImages(yachtId, mappedPhotos);

          if (photoResult.success) {
            toast.dismiss();
            toast.success(`Yacht and ${uploadedPhotos.length} photo${uploadedPhotos.length > 1 ? 's' : ''} added successfully!`);
          } else {
            toast.dismiss();
            toast.error(`Yacht created but photo upload failed: ${photoResult.error}`);
          }
        } else {
          toast.success(`Yacht "${formData.name}" added successfully to ${hostName}'s fleet!`);
        }

        onClose();
        if (onSuccess) onSuccess();
      } else {
        toast.error(result.error || 'Failed to create yacht');
      }
    } catch (error) {
      console.error('Error creating yacht:', error);
      toast.error('An error occurred while creating the yacht');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SailingOutlined />
          <Typography variant="h6">Add Yacht for {hostName}</Typography>
        </Box>
        <Typography variant="caption" color="text.secondary">
          Only yacht name and capacity are required. All other fields are optional.
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={3}>
          {/* Yacht Information Section */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
              Yacht Information
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="Yacht Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Ocean Breeze"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              type="number"
              label="Capacity (Passengers)"
              value={formData.capacity}
              onChange={(e) => setFormData({ ...formData, capacity: e.target.value })}
              inputProps={{ min: 1, max: 200 }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Brief description of the yacht..."
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              select
              label="Yacht Type"
              value={formData.yachtType}
              onChange={(e) => setFormData({ ...formData, yachtType: e.target.value as any })}
            >
              <MenuItem value="">Select Type</MenuItem>
              {YACHT_TYPES.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="Year Built"
              value={formData.year}
              onChange={(e) => setFormData({ ...formData, year: e.target.value })}
              inputProps={{ min: 1950, max: new Date().getFullYear() + 1 }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              select
              label="Manufacturer"
              value={formData.manufacturer}
              onChange={(e) => setFormData({ ...formData, manufacturer: e.target.value })}
            >
              <MenuItem value="">Select Manufacturer</MenuItem>
              {YACHT_MANUFACTURERS.map((make) => (
                <MenuItem key={make} value={make}>
                  {make}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Model"
              value={formData.model}
              onChange={(e) => setFormData({ ...formData, model: e.target.value })}
              placeholder="e.g., Manhattan 55"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Registration Number"
              value={formData.registrationNumber}
              onChange={(e) => setFormData({ ...formData, registrationNumber: e.target.value.toUpperCase() })}
              placeholder="e.g., YCH-001234"
              helperText="Must be unique"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Home Port"
              value={formData.homeport}
              onChange={(e) => setFormData({ ...formData, homeport: e.target.value })}
              placeholder="e.g., Lagos"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Marina"
              value={formData.marina}
              onChange={(e) => setFormData({ ...formData, marina: e.target.value })}
              placeholder="e.g., Lagos Marina"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Type"
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              placeholder="e.g., luxury, sport"
              helperText="Yacht category (e.g., luxury, sport, charter)"
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Yacht Features (e.g., Air Conditioning, WiFi, Jet Ski):
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Add feature..."
                value={newFeature}
                onChange={(e) => setNewFeature(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addFeature()}
              />
              <Button variant="outlined" onClick={addFeature}>
                Add
              </Button>
            </Box>
            {formData.features.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {formData.features.map((feature) => (
                  <Chip
                    key={feature}
                    label={feature}
                    onDelete={() => removeFeature(feature)}
                    color="primary"
                    variant="outlined"
                  />
                ))}
              </Box>
            )}
          </Grid>

          {/* Specifications Section */}
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2, mb: 2 }}>
              Specifications
            </Typography>
          </Grid>

          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="Length"
              value={formData.length}
              onChange={(e) => setFormData({ ...formData, length: e.target.value })}
              placeholder="e.g., 55ft"
              helperText="Including unit (ft/m)"
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              type="number"
              label="Crew Size"
              value={formData.crew}
              onChange={(e) => setFormData({ ...formData, crew: e.target.value })}
              placeholder="e.g., 4"
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              type="number"
              label="Bedrooms"
              value={formData.bedrooms}
              onChange={(e) => setFormData({ ...formData, bedrooms: e.target.value })}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              type="number"
              label="Bathrooms"
              value={formData.bathrooms}
              onChange={(e) => setFormData({ ...formData, bathrooms: e.target.value })}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Speed"
              value={formData.speed}
              onChange={(e) => setFormData({ ...formData, speed: e.target.value })}
              placeholder="e.g., 25 knots"
              helperText="Including unit (knots/mph)"
            />
          </Grid>

          {/* Captain Section */}
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2, mb: 2 }}>
              Captain Information
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Captain Name"
              value={formData.captainName}
              onChange={(e) => setFormData({ ...formData, captainName: e.target.value })}
              placeholder="e.g., Captain John Smith"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Captain Experience"
              value={formData.captainExperience}
              onChange={(e) => setFormData({ ...formData, captainExperience: e.target.value })}
              placeholder="e.g., 15 years"
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Captain Languages:
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Add language..."
                value={newLanguage}
                onChange={(e) => setNewLanguage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addLanguage()}
              />
              <Button variant="outlined" onClick={addLanguage}>
                Add
              </Button>
            </Box>
            {formData.captainLanguages.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {formData.captainLanguages.map((lang) => (
                  <Chip
                    key={lang}
                    label={lang}
                    onDelete={() => removeLanguage(lang)}
                    color="secondary"
                    variant="outlined"
                  />
                ))}
              </Box>
            )}
          </Grid>

          {/* Location */}
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2, mb: 2 }}>
              Location
            </Typography>
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Country"
              value={formData.country}
              onChange={(e) => setFormData({ ...formData, country: e.target.value })}
              placeholder="e.g., Nigeria"
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="City"
              value={formData.city}
              onChange={(e) => setFormData({ ...formData, city: e.target.value })}
              placeholder="e.g., Lagos"
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              select
              label="State"
              value={formData.state}
              onChange={(e) => setFormData({ ...formData, state: e.target.value })}
            >
              <MenuItem value="">Select State</MenuItem>
              {NIGERIAN_STATES.map(state => (
                <MenuItem key={state} value={state}>{state}</MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Location Marina"
              value={formData.locationMarina}
              onChange={(e) => setFormData({ ...formData, locationMarina: e.target.value })}
              placeholder="e.g., Marina Lagos"
              helperText="Marina where yacht is located"
            />
          </Grid>

          {/* Pricing Section */}
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2, mb: 2 }}>
              <AttachMoney sx={{ fontSize: 18, mr: 0.5, verticalAlign: 'middle' }} />
              Pricing
            </Typography>
          </Grid>

          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              select
              label="Currency"
              value={formData.currency}
              onChange={(e) => setFormData({ ...formData, currency: e.target.value as CurrencyCode })}
            >
              {Object.entries(CURRENCY_CONFIG).map(([code, config]) => (
                <MenuItem key={code} value={code}>
                  {config.symbol} {config.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              type="number"
              label="Hourly Rate"
              value={formData.hourly}
              onChange={(e) => setFormData({ ...formData, hourly: e.target.value })}
              InputProps={{
                startAdornment: <InputAdornment position="start">{CURRENCY_CONFIG[formData.currency].symbol}</InputAdornment>
              }}
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              type="number"
              label="Half Day (4hrs)"
              value={formData.halfDay}
              onChange={(e) => setFormData({ ...formData, halfDay: e.target.value })}
              InputProps={{
                startAdornment: <InputAdornment position="start">{CURRENCY_CONFIG[formData.currency].symbol}</InputAdornment>
              }}
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              type="number"
              label="Full Day (8hrs)"
              value={formData.fullDay}
              onChange={(e) => setFormData({ ...formData, fullDay: e.target.value })}
              InputProps={{
                startAdornment: <InputAdornment position="start">{CURRENCY_CONFIG[formData.currency].symbol}</InputAdornment>
              }}
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              type="number"
              label="Full Day Hours"
              value={formData.fullDayHours}
              onChange={(e) => setFormData({ ...formData, fullDayHours: e.target.value })}
              helperText="e.g., 8"
              inputProps={{ min: 1, max: 24 }}
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              type="number"
              label="Weekly Rate"
              value={formData.weekly}
              onChange={(e) => setFormData({ ...formData, weekly: e.target.value })}
              InputProps={{
                startAdornment: <InputAdornment position="start">{CURRENCY_CONFIG[formData.currency].symbol}</InputAdornment>
              }}
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              type="number"
              label="Security Deposit"
              value={formData.securityDeposit}
              onChange={(e) => setFormData({ ...formData, securityDeposit: e.target.value })}
              InputProps={{
                startAdornment: <InputAdornment position="start">{CURRENCY_CONFIG[formData.currency].symbol}</InputAdornment>
              }}
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              select
              label="Fuel Policy"
              value={formData.fuelPolicy}
              onChange={(e) => setFormData({ ...formData, fuelPolicy: e.target.value as any })}
            >
              <MenuItem value="">Select Policy</MenuItem>
              {FUEL_POLICIES.map((policy) => (
                <MenuItem key={policy.value} value={policy.value}>
                  {policy.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.cateringAvailable}
                  onChange={(e) => setFormData({ ...formData, cateringAvailable: e.target.checked })}
                />
              }
              label="Catering Available"
            />
          </Grid>

          {formData.cateringAvailable && (
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Catering Cost Per Person"
                value={formData.cateringCostPerPerson}
                onChange={(e) => setFormData({ ...formData, cateringCostPerPerson: e.target.value })}
                InputProps={{
                  startAdornment: <InputAdornment position="start">{CURRENCY_CONFIG[formData.currency].symbol}</InputAdornment>
                }}
              />
            </Grid>
          )}

          {/* Insurance Section */}
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2, mb: 2 }}>
              Insurance & Safety
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.hasInsurance}
                  onChange={(e) => setFormData({ ...formData, hasInsurance: e.target.checked })}
                />
              }
              label="Has Insurance"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.hasSafetyEquipment}
                  onChange={(e) => setFormData({ ...formData, hasSafetyEquipment: e.target.checked })}
                />
              }
              label="Has Safety Equipment"
            />
          </Grid>

          {formData.hasInsurance && (
            <>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Insurance Type"
                  value={formData.insuranceType}
                  onChange={(e) => setFormData({ ...formData, insuranceType: e.target.value })}
                  placeholder="e.g., Marine Insurance"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Insurance Expiry Date"
                  value={formData.insuranceExpiryDate}
                  onChange={(e) => setFormData({ ...formData, insuranceExpiryDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </>
          )}

          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Safety Certifications (select all that apply):
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {SAFETY_CERTIFICATIONS.map((cert) => (
                <Chip
                  key={cert}
                  label={cert}
                  onClick={() => toggleCertification(cert)}
                  color={formData.safetyCertifications.includes(cert) ? 'primary' : 'default'}
                  variant={formData.safetyCertifications.includes(cert) ? 'filled' : 'outlined'}
                />
              ))}
            </Box>
          </Grid>

          {/* Availability */}
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2, mb: 2 }}>
              Availability
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.instantBooking}
                  onChange={(e) => setFormData({ ...formData, instantBooking: e.target.checked })}
                />
              }
              label="Enable Instant Booking"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="Minimum Rental Hours"
              value={formData.minimumRentalHours}
              onChange={(e) => setFormData({ ...formData, minimumRentalHours: e.target.value })}
              inputProps={{ min: 1, max: 24 }}
            />
          </Grid>

          {/* Yacht Photos */}
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2, mb: 2 }}>
              <CloudUpload sx={{ fontSize: 18, mr: 0.5, verticalAlign: 'middle' }} />
              Yacht Photos (Optional)
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Button
              variant="outlined"
              component="label"
              startIcon={<CloudUpload />}
              disabled={uploadedPhotos.length >= 15}
              fullWidth
            >
              Upload Photos ({uploadedPhotos.length}/15)
              <input
                type="file"
                hidden
                multiple
                accept="image/*"
                onChange={handlePhotoUpload}
              />
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Upload up to 15 yacht photos (max 10MB each)
            </Typography>
          </Grid>

          {uploadedPhotos.length > 0 && (
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {uploadedPhotos.map((photo, index) => (
                  <Box
                    key={index}
                    sx={{
                      position: 'relative',
                      width: 100,
                      height: 100,
                      borderRadius: 1,
                      overflow: 'hidden',
                      border: '1px solid',
                      borderColor: 'divider'
                    }}
                  >
                    <img
                      src={photo.imageData}
                      alt={photo.fileName}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        backgroundColor: 'error.main',
                        borderRadius: '50%',
                        width: 24,
                        height: 24,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: 'error.dark' }
                      }}
                      onClick={() => handleRemovePhoto(index)}
                    >
                      <Delete sx={{ fontSize: 16, color: 'white' }} />
                    </Box>
                  </Box>
                ))}
              </Box>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? 'Creating...' : 'Add Yacht'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
